import { Spacing } from '@persona-id/design-tokens';
import { ArrowLeftIcon, DocumentIcon, GlobeIcon, XIcon } from '@persona-id/design-tokens/icons';
import Fuse from 'fuse.js';
import { snakeCase } from 'lodash/fp';
import { ReactNode, useMemo, useState } from 'react';
import { MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled, { keyframes } from 'styled-components';

import TemplateModuleCard from 'dashboard/apps/ReportTemplateEditor/TemplateModuleCard';
import AddOnInquiry from 'dashboard/components/AddOnInquiry/AddOnInquiry';
import PlanUpgradeFlow from 'dashboard/components/PlanUpgradeFlow';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { ProductFeatureName } from 'dashboard/models/organization';
import { AvailableOrganizationPlan } from 'dashboard/models/organization-plan';
import { OrganizationPlanMigration } from 'dashboard/models/organization-plan-migration';
import {
  ReportBuilderName,
  ReportTemplate,
  ReportTemplateBuilder,
} from 'dashboard/models/report-template';
import { BUILDER_LABELS } from 'dashboard/views/DashboardReportTemplatesView/constants';
import CreateReportTemplatePage from 'dashboard/views/DashboardReportTemplatesView/CreateReportTemplatePage';
import { Heading, Paragraph } from 'design-system/components/Text';
import { TransitionDuration } from 'design-system/variables/transitions';
import useDebounced from 'lib/hooks/useDebounced';
import { ApiModel } from 'lib/interfaces/api';

interface CreateReportTemplateModalProps {
  reportTemplateBuilders: ReportTemplateBuilder[];
  loadingAvailablePlans: boolean;
  handleCloseModal: () => void;
  handleCreateReportTemplate: (builderName: string) => Promise<void>;
  handleLoadAvailablePlans: (
    requiredProductFeatures: ProductFeatureName[],
  ) => Promise<AvailableOrganizationPlan[]>;
  handleCreateOrganizationPlanMigration: (
    inquiryId: string,
    selectedPlanKey: string,
  ) => Promise<OrganizationPlanMigration>;
  showMessage: (message: string) => void;
  updatePayment: (stripeId: string) => Promise<void>;
}

const iconForReportType = (type: ReportTemplate['type']) => {
  switch (type) {
    case ApiModel.ReportTemplateWatchlist:
      return <GlobeIcon />;
    default:
      return <DocumentIcon />;
  }
};

const CreateReportTemplateModal = ({
  reportTemplateBuilders,
  loadingAvailablePlans,
  handleCloseModal,
  handleCreateReportTemplate,
  handleLoadAvailablePlans,
  handleCreateOrganizationPlanMigration,
  showMessage,
  updatePayment,
}: CreateReportTemplateModalProps) => {
  const [selectedBuilder, setSelectedBuilder] = useState<ReportTemplateBuilder | undefined>(
    undefined,
  );

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSetSearchTerm] = useDebounced(setSearchTerm, 500, {
    leading: false,
    trailing: true,
  });

  const [showAddOnInquiry, setShowAddOnInquiry] = useState(false);
  const [showPlanUpgradeFlow, setShowPlanUpgradeFlow] = useState(false);

  reportTemplateBuilders = [
    {
      name: ReportBuilderName.AddressLookup,
      reportTypes: [ApiModel.ReportTemplateAddressLookup],
      requiredProductFeatures: [ProductFeatureName.ReportsAddressLookupCreate],
      requiresOrderForm: false,
    },
    {
      name: ReportBuilderName.AdverseMedia,
      reportTypes: [ApiModel.ReportTemplateAdverseMedia],
      requiredProductFeatures: [ProductFeatureName.ReportsAdverseMediaCreate],
      requiresOrderForm: false,
    },
    {
      name: ReportBuilderName.BusinessWatchlist,
      reportTypes: [ApiModel.ReportTemplateBusinessWatchlist],
      requiredProductFeatures: [ProductFeatureName.ReportsBusinessWatchlistCreate],
      requiresOrderForm: true,
    },
    {
      name: ReportBuilderName.BusinessAdverseMedia,
      reportTypes: [ApiModel.ReportTemplateBusinessAdverseMedia],
      requiredProductFeatures: [ProductFeatureName.ReportsBusinessAdverseMediaCreate],
      requiresOrderForm: true,
    },
    {
      name: ReportBuilderName.CryptoAddressWatchlist,
      reportTypes: [ApiModel.ReportTemplateCryptoAddressWatchlist],
      requiredProductFeatures: [ProductFeatureName.ReportsCryptoAddressWatchlistCreate],
      requiresOrderForm: false,
    },
    {
      name: ReportBuilderName.EmailAddress,
      reportTypes: [ApiModel.ReportTemplateEmailAddress],
      requiredProductFeatures: [ProductFeatureName.ReportsEmailAddressCreate],
      requiresOrderForm: false,
    },
    {
      name: ReportBuilderName.PhoneNumber,
      reportTypes: [ApiModel.ReportTemplatePhoneNumber],
      requiredProductFeatures: [ProductFeatureName.ReportsPhoneNumberCreate],
      requiresOrderForm: false,
    },
    {
      name: ReportBuilderName.PoliticallyExposedPerson,
      reportTypes: [ApiModel.ReportTemplatePoliticallyExposedPerson],
      requiredProductFeatures: [ProductFeatureName.ReportsPoliticallyExposedPersonCreate],
      requiresOrderForm: false,
    },
    {
      name: ReportBuilderName.Watchlist,
      reportTypes: [ApiModel.ReportTemplateWatchlist],
      requiredProductFeatures: [ProductFeatureName.ReportsWatchlistCreate],
      requiresOrderForm: false,
    },
  ];

  const fuse = useMemo(() => {
    const buildersWithLabels = reportTemplateBuilders.map((builder) => ({
      ...builder,
      ...BUILDER_LABELS[builder.name],
    }));
    return new Fuse(buildersWithLabels, {
      shouldSort: false,
      threshold: 0.3,
      ignoreLocation: true,
      minMatchCharLength: 2,
      keys: [
        { name: 'title', weight: 0.7 },
        { name: 'description', weight: 0.3 },
      ],
    });
  }, [reportTemplateBuilders]);

  const searchResults: Array<ReportTemplateBuilder> | null = useMemo(
    () => (searchTerm !== '' ? fuse.search(searchTerm).map(({ item }) => item) : null),
    [fuse, searchTerm],
  );

  const handleNavBack = () => {
    setShowAddOnInquiry(false);
    setShowPlanUpgradeFlow(false);
    setSelectedBuilder(undefined);
  };

  const cardsToShow = useMemo<React.ReactNode[]>(() => {
    const rows: ReactNode[] = [];
    reportTemplateBuilders.forEach((builder) => {
      // skip row if search in progress and row is not included in results
      if (searchResults != null && !searchResults.some((result) => result.name === builder.name)) {
        return;
      }

      const { name, reportTypes, requiredProductFeatures, requiresOrderForm } = builder;
      const labels = BUILDER_LABELS[name];

      if (!labels || !labels?.title || !labels?.description) {
        return;
      }

      const builderCard = (
        <TemplateModuleCard
          size='md'
          id={`${name}-builder-card`}
          key={`${name}-builder-card`}
          selected={selectedBuilder ? selectedBuilder.name === builder.name : false}
          title={labels.title}
          description={labels.description}
          icon={iconForReportType(reportTypes[0])}
          requiredProductFeatures={requiredProductFeatures}
          requiresOrderForm={requiresOrderForm}
          onClick={() => {
            setSelectedBuilder(builder);
          }}
        />
      );
      rows.push(builderCard);
    });

    return rows;
  }, [reportTemplateBuilders, selectedBuilder]);

  return (
    <>
      {showPlanUpgradeFlow ? (
        <PlanUpgradeFlow
          requestedFeatureName={`report_template_${snakeCase(selectedBuilder?.name || '')}`}
          requiredProductFeatures={selectedBuilder?.requiredProductFeatures || []}
          loadingAvailablePlans={loadingAvailablePlans}
          handleLoadAvailablePlans={handleLoadAvailablePlans}
          handleSubmitBillingInfo={updatePayment}
          handleCreateOrganizationPlanMigration={handleCreateOrganizationPlanMigration}
          showMessage={showMessage}
          onNavBack={() => setShowPlanUpgradeFlow(false)}
          onClose={handleCloseModal}
        />
      ) : showAddOnInquiry ? (
        <ModalContainer>
          <ModalHeader
            prefixIcon={<ArrowLeftIcon size={20} />}
            onPrefixIconClick={() => setShowAddOnInquiry(false)}
            onClose={handleCloseModal}
          >
            Request {selectedBuilder ? BUILDER_LABELS[selectedBuilder.name].title : 'template'}
          </ModalHeader>
          <ModalBody>
            <AddOnInquiry
              requestedProductName={`report_template_${snakeCase(selectedBuilder?.name || '')}`}
              onComplete={handleCloseModal}
            />
          </ModalBody>
        </ModalContainer>
      ) : (
        <StyledModalContainer>
          <StyledModalHeader expanded={!selectedBuilder}>
            <HeaderTitleWrapper>
              {selectedBuilder && (
                <StyledIconNavBack id='create-template__nav-back' onClick={handleNavBack} />
              )}
              <Heading size='sm'>
                {selectedBuilder ? BUILDER_LABELS[selectedBuilder.name].title : 'Choose a template'}
              </Heading>
              <StyledIconX
                id='create-template__close-modal'
                data-test='create-template__close-modal'
                onClick={handleCloseModal}
              />
            </HeaderTitleWrapper>
            {!selectedBuilder && (
              <SearchFilterRow>
                <SearchInput
                  autoFocus
                  size='md'
                  id='report-templates-template__search'
                  placeholder='Search'
                  handleInput={(searchTerm) => debouncedSetSearchTerm(searchTerm)}
                  value={searchTerm}
                  title={null}
                />
              </SearchFilterRow>
            )}
          </StyledModalHeader>
          {selectedBuilder ? (
            <FullViewWrapper>
              <AnimatedWrapper>
                <CreateReportTemplatePage
                  builderName={selectedBuilder.name}
                  description={BUILDER_LABELS[selectedBuilder.name].description}
                  helpCenterLink={BUILDER_LABELS[selectedBuilder.name].helpCenterLink}
                  requiredProductFeatures={selectedBuilder.requiredProductFeatures}
                  requiresOrderForm={selectedBuilder.requiresOrderForm}
                  handleCreateReportTemplate={handleCreateReportTemplate}
                  handlePlanUpgradeFlow={() => {
                    setShowPlanUpgradeFlow(true);
                  }}
                  handleAddOnFlow={() => setShowAddOnInquiry(true)}
                />
              </AnimatedWrapper>
            </FullViewWrapper>
          ) : (
            <SelectBuilderView cardsToShow={cardsToShow} />
          )}
        </StyledModalContainer>
      )}
    </>
  );
};

const CARD_HEIGHT = 156;
const HEADER_EXPANDED = 136;
const HEADER_COLLAPSED = 72;

/* modal body components */
const StyledModalContainer = styled(ModalContainer)`
  position: relative;
  height: clamp(700px, 85vh, 850px);
  width: clamp(980px, 90vw, 1100px);
  padding: 0;
`;

/* modal header components */
const StyledModalHeader = styled.div<{ expanded: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  border-bottom: 1px solid ${({ theme }) => theme.color.border.secondary};
  height: ${({ expanded }) => (expanded ? HEADER_EXPANDED : HEADER_COLLAPSED)}px;
  transition: height ${TransitionDuration.Fast} linear;
  z-index: 1;
  padding: ${Spacing[5]};
  width: 100%;
`;

const HeaderTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const FullViewWrapper = styled.div`
  position: absolute;
  background: none;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const slideIn = keyframes`
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(0);
  }
`;

const AnimatedWrapper = styled.div`
  position: relative;
  height: 100%;
  padding-top: ${HEADER_COLLAPSED}px;
  animation: ${slideIn} ${TransitionDuration.Fast} forwards;
`;

const StyledIconX = styled(XIcon).attrs(() => ({ size: 24 }))`
  cursor: pointer;
  margin-left: auto;
`;

const StyledIconNavBack = styled(ArrowLeftIcon).attrs(() => ({ size: 24 }))`
  cursor: pointer;
  flex: none;
  height: 20px;
  width: 20px;
  margin-right: ${Spacing[5]};
  svg {
    color: ${({ theme }) => theme.color.text.primary};
  }
`;

const SearchFilterRow = styled.div`
  display: grid;
  grid-template-columns: 2fr minmax(300px, 1fr);
  grid-gap: ${Spacing[4]};
  margin-top: ${Spacing[4]};
  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: none;
    grid-template-rows: auto auto;
  }
`;

const SelectBuilderView = ({ cardsToShow }: { cardsToShow: ReactNode[] }) => {
  return cardsToShow.length > 0 ? (
    <CardsContainer>{cardsToShow}</CardsContainer>
  ) : (
    <NoResultsWrapper>
      <Paragraph size='md'>
        No matched results. Try changing your search and/or filter(s).
      </Paragraph>
    </NoResultsWrapper>
  );
};

const CardsContainer = styled.div`
  display: grid;
  grid-gap: ${Spacing[4]};
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: ${CARD_HEIGHT}px;
  height: 100%;
  overflow-y: scroll;
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;
  background-color: ${({ theme }) => theme.color.background.secondary};
  padding: ${Spacing[5]};
`;

const NoResultsWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  padding-top: ${Spacing[8]};
  background-color: ${({ theme }) => theme.color.background.secondary};
  border-radius: inherit;
`;

export default CreateReportTemplateModal;
